let database;

const loadActiveBoard = function(boards) {
  try {
    const saved = localStorage.getItem('stickii-active-board');
    if (boards.some(board => board.id === saved)) return saved;
  } catch {
    // Board data remains available in IndexedDB without localStorage.
  }

  return boards[0]?.id || 'main';
};

const saveActiveBoard = function(id) {
  try {
    localStorage.setItem('stickii-active-board', id);
  } catch {
    // Switching boards still works if preferences cannot be saved.
  }
};

const openDatabase = function() {
  if (!database) {
    database = new Promise((resolve, reject) => {
      const request = indexedDB.open('stickii', 1);

      request.onupgradeneeded = () => request.result.createObjectStore('boards');
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Close other stickii tabs and reload to enable saving.'));
    });
  }

  return database;
};

const loadNotes = async function(boardId = 'main') {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const request = db.transaction('boards', 'readonly').objectStore('boards').get(boardId);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const saveNotes = async function(notes, boardName, boardId = 'main') {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('boards', 'readwrite');
    const store = transaction.objectStore('boards');

    // Ignore late autosave flushes from a board that has just been deleted.
    const deleted = store.get(`${boardId}:deleted`);

    deleted.onsuccess = () => {
      if (deleted.result) return;

      store.put(notes, boardId);
      store.put(boardName, boardId === 'main' ? 'name' : `${boardId}:name`);
    };
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error || new Error('Your board could not be saved.'));
    transaction.onerror = () => reject(transaction.error);
  });
};

const loadBoardName = async function(boardId = 'main') {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const request = db.transaction('boards', 'readonly').objectStore('boards').get(boardId === 'main' ? 'name' : `${boardId}:name`);

    request.onsuccess = () => resolve(request.result ?? 'my board');
    request.onerror = () => reject(request.error);
  });
};

const listBoards = async function() {
  const db = await openDatabase();

  const ids = await new Promise((resolve, reject) => {
    const request = db.transaction('boards', 'readonly').objectStore('boards').get('board-list');

    request.onsuccess = () => resolve(request.result || ['main']);
    request.onerror = () => reject(request.error);
  });

  return Promise.all(ids.map(async id => ({
    id,
    name: await loadBoardName(id)
  })));
};

const createBoard = async function(boards) {
  const db = await openDatabase();

  const board = {
    id: `board:${crypto.randomUUID()}`,
    name: `board ${boards.length + 1}`
  };

  await new Promise((resolve, reject) => {
    const transaction = db.transaction('boards', 'readwrite');
    const store = transaction.objectStore('boards');

    store.put([...boards.map(item => item.id), board.id], 'board-list');
    store.put([], board.id);
    store.put(board.name, `${board.id}:name`);
    transaction.oncomplete = resolve;
    transaction.onabort = () => reject(transaction.error);
    transaction.onerror = () => reject(transaction.error);
  });

  return board;
};

const importBoards = async function(existing, imported) {
  const db = await openDatabase();

  const boards = imported.map(board => ({
    ...board,
    id: `board:${crypto.randomUUID()}`
  }));

  await new Promise((resolve, reject) => {
    const transaction = db.transaction('boards', 'readwrite');
    const store = transaction.objectStore('boards');

    store.put([...existing.map(board => board.id), ...boards.map(board => board.id)], 'board-list');
    boards.forEach(board => {
      store.put(board.notes, board.id);
      store.put(board.name, `${board.id}:name`);
    });
    transaction.oncomplete = resolve;
    transaction.onabort = () => reject(transaction.error);
    transaction.onerror = () => reject(transaction.error);
  });

  return boards.map(({id, name}) => ({
    id,
    name
  }));
};

const deleteBoard = async function(boards, boardId) {
  const db = await openDatabase();
  const remaining = boards.filter(board => board.id !== boardId);

  // Deleting the final board creates an empty board to keep navigation usable.
  const replacement = remaining.length ? null : {
    id: `board:${crypto.randomUUID()}`,
    name: 'my board'
  };

  if (replacement) remaining.push(replacement);

  await new Promise((resolve, reject) => {
    const transaction = db.transaction('boards', 'readwrite');
    const store = transaction.objectStore('boards');

    store.put(true, `${boardId}:deleted`);
    store.delete(boardId);
    store.delete(boardId === 'main' ? 'name' : `${boardId}:name`);
    store.put(remaining.map(board => board.id), 'board-list');

    if (replacement) {
      store.put([], replacement.id);
      store.put(replacement.name, `${replacement.id}:name`);
    }

    transaction.oncomplete = resolve;
    transaction.onabort = () => reject(transaction.error);
    transaction.onerror = () => reject(transaction.error);
  });

  return remaining;
};

export {
  loadActiveBoard,
  saveActiveBoard,
  loadNotes,
  saveNotes,
  loadBoardName,
  listBoards,
  createBoard,
  importBoards,
  deleteBoard
};
